import { Api } from 'tools/api';
import { ApiRequest, ApiRequestObservable, PagedList } from 'framework/api';
import { Asset, AssetTarget } from 'framework/entities/asset';
import { Geotag } from 'framework/entities/geotag';
import { ApiRequestContext } from 'tools/apiRequestContext';
import { Monitor } from 'framework/entities/monitor';
import { AssetGroup } from 'framework/entities/assetGroup';
import { UrlBuilder } from 'tools/urlBuilder';
import { AssetDetails } from 'areas/assets/details/types/assetDetails';
import { RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { AssetGridItem } from 'areas/assets/assets-grid/types';
import { GridDataItem } from 'controls/grid/gridDataItem';
import { GridStore } from 'controls/grid/gridStore';
import { NamedItem } from 'areas/services/services-grid/api';
import { Dependency } from 'areas/assets/monitors/api';
import { ApplicationState } from 'framework/applicationState';
import { AssetHistoryData, AssetMonitor, MonitorTypeItem } from 'areas/assets/asset-health-history/types';
import { TimePeriod } from 'controls/react/form/timePeriodSelector';
import { TimePeriodType } from 'controls/react/form/timePeriodType';

class AssetsApi {
	static getDynamicSearchUrl(includeSubaccounts: string, accountId?: string) {
		return Api.accountRoot(accountId) + 'assets/search/lite?includeSubaccounts=' + includeSubaccounts;
	}

	static getRootUrl(accountId?: string, sessionId?: string) {
		return Api.sessionAndAccountRoot(accountId, sessionId) + 'assets/';
	}

	static getAssetUrl(assetId: string, accountId?: string, sessionId?: string) {
		return AssetsApi.getRootUrl(accountId, sessionId) + assetId + '/';
	}

	static getAssetDetailsUrl(assetId: string) {
		return Api.accountRoot() + 'assets/' + assetId + '/assetDetails';
	}

	static getAssetLiteUrl({
		accountId,
		sessionId,
		includeSubaccounts,
	}: { accountId?: string; sessionId?: string; includeSubaccounts?: boolean } = {}) {
		return AssetsApi.getRootUrl(accountId, sessionId) + 'assets/lite?includeSubaccounts=' + includeSubaccounts;
	}

	static getAssetsLite({
		accountId,
		sessionId,
		cache,
		includeSubaccounts = false,
	}: {
		accountId?: string;
		sessionId?: string;
		cache?: object;
		includeSubaccounts?: boolean;
	}) {
		return Api.fetch(
			AssetsApi.getRootUrl(accountId, sessionId) + 'lite?includeSubaccounts=' + includeSubaccounts,
			null,
			cache,
			'assets' + accountId,
		);
	}

	static getHealthIndex(monitorId: string = null, assetId: string, accountId?: string, sessionId?: string) {
		let url = AssetsApi.getAssetUrl(assetId, accountId, sessionId) + 'health';

		if (monitorId != null && monitorId !== '') {
			url += '/monitors/' + monitorId;
		}

		return Api.fetch(url);
	}

	static getMonitorsUrl(assetId: string, accountId?: string, sessionId?: string) {
		return AssetsApi.getAssetUrl(assetId, accountId, sessionId) + 'health/monitors';
	}

	static getMonitors(assetId: string, context: ApiRequestContext) {
		return Api.fetch(
			AssetsApi.getMonitorsUrl(assetId, context?.accountId, context?.sessionId),
			null,
			context?.cache,
			'asset-monitors' + assetId,
		);
	}
}

class AssetGroupsApi {
	static getRootUrl(accountId?: string, sessionId?: string) {
		return Api.sessionAndAccountRoot(accountId, sessionId) + 'assetGroups/';
	}

	static getGroups(accountId?: string, sessionId?: string, cache?: object) {
		return Api.fetch(AssetGroupsApi.getRootUrl(accountId, sessionId), null, cache, 'assetGroups' + accountId);
	}

	static getGroupMembers(assetGroupId: string) {
		return Api.fetch(AssetGroupsApi.getRootUrl() + assetGroupId + '/members');
	}

	static getDynamicSearchUrl(includeSubaccounts: string) {
		return Api.accountRoot() + 'assetGroups/search/lite?includeSubaccounts=' + includeSubaccounts;
	}
}

export { AssetsApi, AssetGroupsApi };

export type GetAssetGroupsOptions = {
	accountId?: string;
};

export function getAssetGroups(options: GetAssetGroupsOptions = {}) {
	return new ApiRequestObservable<AssetGroup[]>({
		url: 'assetGroups',
		accountId: options.accountId,
		accountBased: true,
	});
}

export type GetAssetGroupAssetsOptions = {
	assetGroupId: string;
	accountId?: string;
	agentsOnly?: boolean;
	systemType?: string;
};

export function getAssetGroupAssets(options: GetAssetGroupAssetsOptions) {
	if (!options.assetGroupId) return null;

	const urlBuilder = new UrlBuilder(`assetGroups/${options.assetGroupId}/members`)
		.addPath('types/AGENT', !!options.agentsOnly)
		.add('systemType', options.systemType, !!options.systemType);

	return new ApiRequest<Asset[]>({
		url: urlBuilder.build(),
		accountId: options.accountId,
		accountBased: true,
	});
}

export type GetAssetGroupAssetsLiteArgs = {
	geotag?: boolean;
	assetGroupIds: string[];
	accountId?: string;
};
export function getAssetGroupsAssetsLite(args: GetAssetGroupAssetsLiteArgs) {
	return new ApiRequest<Asset[]>({
		url: 'assetGroups/members/lite',
		accountId: args.accountId,
		accountBased: true,
		method: 'POST',
		payload: {
			assetGroupIds: args.assetGroupIds,
			geotag: args.geotag ?? false,
		},
	});
}

export function getAsset(assetId: string, accountId: string = null) {
	return new ApiRequest<Asset>({
		url: `assets/${assetId}`,
		accountId: accountId,
		accountBased: true,
	});
}

export type GetAssetForEditArgs = {
	assetId: string;
	update: boolean;
};

export type AssetForEdit = {
	iconPack: string;
};

export function getAssetForEdit(args: GetAssetForEditArgs) {
	return new ApiRequest<AssetForEdit>({
		url: new UrlBuilder(`assets/${args.assetId}`).add('update', args.update).build(),
		accountBased: true,
	});
}

export type SaveAssetArgs = {
	accountId: string;
	asset: {
		id: string;
		name: string;
		description: string;
		information: string;
		systemType: string;
		assetGroups: string[];
		targets: AssetTarget[];
		tags: string[];
		imageId: string;
		iconPack: string;
		calendar: string;
		maintenanceCalendars: unknown[];
		manualGeotag: Geotag;
	};
};

export function saveAsset(args: SaveAssetArgs) {
	return new ApiRequest<Asset>({
		url: 'assets',
		method: 'POST',
		accountId: args.accountId,
		accountBased: true,
		payload: args.asset,
	});
}

export function searchAssetsByName(name: string) {
	return new ApiRequest<PagedList<Asset>>({
		url: 'assets/search/lite',
		method: 'POST',
		accountBased: true,
		includeSubaccounts: false,
		payload: {
			filter: {
				logic: 'and',
				filters: [{ field: 'name', operator: 'eq', value: name }],
			},
		},
	});
}

export type GetAssetDetailsArgs = {
	assetId: string;
	accountId?: string;
};
export function getAssetDetails(args: GetAssetDetailsArgs) {
	return new ApiRequest<AssetDetails>({
		url: `assets/${args.assetId}/assetDetails`,
		accountId: args?.accountId,
		accountBased: true,
		responseType: AssetDetails,
	});
}

export enum AssetsDataset {
	Lite,
	Full,
}

export type GetAssetsOptions = {
	accountId?: string;
	dataset?: AssetsDataset;
	hasGeotag?: boolean;
	shared?: boolean;
	agentsOnly?: boolean;
	includeSubaccounts?: boolean;
	systemType?: string;
};

export function getAssets(options: GetAssetsOptions = {}) {
	if (options.hasGeotag && options.dataset != AssetsDataset.Lite) {
		console.warn('geotag flag works only for /lite ');
	}

	const urlBuilder = new UrlBuilder('assets')
		.addPath('lite', options.dataset == AssetsDataset.Lite)
		.add('geotag', 'true', !!options.hasGeotag)
		.add('assetType', 'AGENT', !!options.agentsOnly)
		.add('includeShared', 'true', !!options.shared)
		.add('includeSubaccounts', 'true', !!options.includeSubaccounts)
		.add('systemType', options.systemType, !!options.systemType);

	return new ApiRequestObservable<Asset[]>({
		url: urlBuilder.build(),
		accountId: options.accountId,
		accountBased: true,
	});
}

export type SearchAssetsLiteArgs = {
	accountIds: string[];
	includeSubaccounts?: boolean;
};

export function searchAssetsLite(args: SearchAssetsLiteArgs) {
	return new ApiRequest<PagedList<Asset>>({
		method: 'POST',
		url: 'assets/search/lite',
		payload: {
			filter: {
				logic: 'or',
				filters: args.accountIds.map((x) => ({ field: 'accountId', operator: 'eq', value: x })),
			},
			page: 1,
			pageSize: 9999,
			skip: 0,
			sort: [{ field: 'name', dir: 'asc' }],
			take: 9999,
		},
		accountBased: true,
		subaccountsFilter: true,
		includeSubaccounts: args.includeSubaccounts,
	});
}

export type SearchAssetGroupsLiteArgs = {
	accountIds: string[];
	includeSubaccounts?: boolean;
};

export function searchAssetGroupsLite(args: SearchAssetGroupsLiteArgs) {
	return new ApiRequest<PagedList<AssetGroup>>({
		method: 'POST',
		url: 'assetGroups/search/lite',
		payload: {
			filter: {
				logic: 'or',
				filters: args.accountIds.map((x) => ({ field: 'accountId', operator: 'eq', value: x })),
			},
			page: 1,
			pageSize: 9999,
			skip: 0,
			sort: [{ field: 'name', dir: 'asc' }],
			take: 9999,
		},
		accountBased: true,
		subaccountsFilter: true,
		includeSubaccounts: args.includeSubaccounts,
	});
}

export function getMonitors(assetId: string, accountId?: string) {
	return new ApiRequest<Monitor[]>({
		url: `assets/${assetId}/health/monitors`,
		accountId: accountId,
		accountBased: true,
		subaccountsFilter: true,
	});
}

export function getExternalTypes() {
	return new ApiRequest<Array<{ value: string; text: string }>>({
		url: `monitors/externalTypes`,
	});
}

const assetsBase = 'assets/summaryList/';
export function getAssetsLite() {
	return new ApiRequest<PagedList<GridDataItem<AssetGridItem>>>({
		url: `${assetsBase}lite`,
		method: 'POST',
		accountBased: true,
		payload: { sort: [{ field: 'name', dir: 'asc' }] },
	});
}

export function getAssetRulesConfiguration() {
	return new ApiRequest<RulesConfiguration>({
		url: `${assetsBase}ruleConfiguration?includeSubaccounts=${ApplicationState.includeSubaccounts}`,
		accountBased: true,
	});
}

export function getAssetGridData() {
	return new ApiRequest<PagedList<GridDataItem<AssetGridItem>>>({
		url: assetsBase,
		accountBased: true,
		method: 'POST',
	});
}

export function setInformation(store: GridStore<AssetGridItem>, informationText: string) {
	return store.getSelectionApiRequest<NamedItem[]>({
		url: `${assetsBase}setInformation`,
		payload: {
			bulkInformation: informationText,
		},
	});
}

export function setInMaintenance(store: GridStore<AssetGridItem>, maintenanceData: object) {
	return store.getSelectionApiRequest<NamedItem[]>({
		url: `${assetsBase}startMaintenance`,
		payload: {
			bulkMaintenance: maintenanceData,
		},
	});
}

export function endMaintenance(store: GridStore<AssetGridItem>) {
	return store.getSelectionApiRequest<NamedItem[]>({
		url: `${assetsBase}endMaintenance`,
	});
}

export function setTags(store: GridStore<AssetGridItem>, tags: string[]) {
	return store.getSelectionApiRequest({
		url: `${assetsBase}tags`,
		payload: {
			bulkTags: tags,
		},
	});
}

export function aknowledge(store: GridStore<AssetGridItem>) {
	return store.getSelectionApiRequest({
		url: `${assetsBase}acknowledgeAssetHealth`,
	});
}

export function validateMerge(parentAssetId: string, store: GridStore<AssetGridItem>) {
	return store.getSelectionApiRequest<{ message: string[] }>({
		url: `${assetsBase}validateMerge`,
		payload: {
			bulkMergeAssetId: parentAssetId,
		},
	});
}

export function merge(parentAssetId: string, store: GridStore<AssetGridItem>) {
	return store.getSelectionApiRequest<{ message: string[] }>({
		url: `${assetsBase}mergeAssets`,
		payload: {
			bulkMergeAssetId: parentAssetId,
		},
	});
}

export function getStaticAssetGroups(accountId?: string) {
	return new ApiRequest<Array<AssetGroup>>({
		url: `assetGroups/lite/?groupType=STATIC`,
		accountBased: true,
		accountId: accountId,
	});
}

export function updateAssetGroup(assetGroupId: string, store: GridStore<AssetGridItem>) {
	return store.getSelectionApiRequest<{ message: string[] }>({
		url: `${assetsBase}updateAssetGroup`,
		payload: {
			bulkAssetGroupId: assetGroupId,
		},
	});
}

export function getAllSelected(store: GridStore<AssetGridItem>) {
	return store.getSelectionApiRequest<Array<Asset>>({
		url: `${assetsBase}selectedItems`,
	});
}

export function getAssetDependencies(id: string) {
	return new ApiRequest<Array<Dependency>>({
		url: `/assets/${id}/dependencies`,
	});
}

export function deleteAsset(id: string) {
	return new ApiRequest<Array<string>>({
		url: `/assets/${id}`,
		method: 'DELETE',
	});
}

export function deleteAssetDependencies(id: string) {
	return new ApiRequest<Array<string>>({
		url: `/assets/${id}/dependencies/deleteAll`,
		method: 'DELETE',
	});
}

export function getAssetMonitorTypes(id: string) {
	return new ApiRequest<Array<MonitorTypeItem>>({
		url: `assets/${id}/monitors/types`,
		accountBased: true,
	});
}

export function getAssetMonitors(args: { id: string; type?: string }) {
	const builder = new UrlBuilder(`assets/${args.id}/health/monitors`).addPath(
		`types/${args.type}`,
		!!args.type && args.type !== 'all',
	);
	return new ApiRequest<Array<AssetMonitor>>({
		url: builder.build(),
		accountBased: true,
	});
}

export type GetAssetHistoryDataArgs = {
	id: string;
	monitorType: string;
	timeZone: string;
	timePeriod?: TimePeriod;
	maxSamples?: number;
	chartEntries?: number;
};
export function getAssetHistoryData(args: GetAssetHistoryDataArgs) {
	const builder = new UrlBuilder(`assets/${args.id}/healthIndexes`)
		.add('timeZone', args.timeZone)
		.add('monitorType', args.monitorType, args.monitorType != 'all');

	if (!!args.timePeriod && args.timePeriod.period == TimePeriodType.LastN) {
		builder.addPath('last' + args.timePeriod.lastNValue);
	}

	if (
		!!args.timePeriod &&
		args.timePeriod.period != TimePeriodType.LastN &&
		args.timePeriod.period != TimePeriodType.None
	) {
		const data = { ...args.timePeriod, timeSelector: args.timePeriod.period };
		builder.addObject(data);
	}
	builder.add('_', new Date().getTime());
	return new ApiRequest<Array<AssetHistoryData>>({
		url: builder.build(),
		accountBased: true,
	});
}
